import { apiClient } from "./client";

export interface TeamResetPasswordBody {
    email: string;
    temporaryPassword: string;
    newPassword: string;
}

interface TeamResetPasswordResponse {
    token?: string;
    accessToken?: string;
    data?: {
        token?: string;
        accessToken?: string;
    };
}

const resolveResetPasswordToken = (response: TeamResetPasswordResponse) =>
    response.token ?? response.accessToken ?? response.data?.token ?? response.data?.accessToken;

export interface TeamResetPasswordResult {
    token: string;
}

export async function resetPassword(body: TeamResetPasswordBody): Promise<TeamResetPasswordResult> {
    const response = await apiClient.post<TeamResetPasswordResponse>('/team/reset-password', body);
    const token = resolveResetPasswordToken(response);
    if (!token) {
        throw new Error("Password reset response did not include an auth token.");
    }
    return { token };
}

export interface TeamMemberApiResult {
    memberId: string;
    kind?: string;
    name: string;
    email: string;
    role: string;
    department?: string | null;
    status: string;
    invited: string;
    lastActive: string | null;
    permissions: string[];
}

export interface TeamMembersResponse {
    success: boolean;
    cards: {
        totalMembers: number;
        activeCount: number;
        pendingCount: number;
        adminCount: number;
    };
    pagination: {
        pageNumber: number;
        pageLimit: number;
        totalResults: number;
        totalPages: number;
    };
    results: TeamMemberApiResult[];
}

/** API enum for member roles */
export const TEAM_MEMBER_ROLES = ["admin", "manager", "executive", "customer_support"] as const;
export type TeamMemberRole = (typeof TEAM_MEMBER_ROLES)[number];

/** POST /team/invite — field names must match API contract */
export interface InviteTeamMemberBody {
    name: string;
    email: string;
    department: string;
    role: TeamMemberRole;
    permissions: string[];
}

export async function teamMembers(
    pageNumber: number,
    pageLimit: number,
    params: { search?: string } & Record<string, unknown> = {}
): Promise<TeamMembersResponse> {
    const { search, ...rest } = params;
    const response = await apiClient.get<TeamMembersResponse>('/team/members', {
        params: {
            pageNumber: String(pageNumber),
            pageLimit: String(pageLimit),
            ...(typeof search === 'string' && search.trim() ? { name: search.trim() } : {}),
            ...rest,
        },
    });
    return response;
}



export async function createTeamMember(body: InviteTeamMemberBody) {
    const response = await apiClient.post('/team/invite', body);
    return response;
}


export async function updateTeamMember(id: string, teamMember: any) {
    const response = await apiClient.patch(`/team/members/${id}`, teamMember);
    return response;
}

export async function deleteTeamMember(id: string) {
    const response = await apiClient.delete(`/team/members/${id}`);
    return response;
}

